import { useEffect } from "react";
import { createPortal } from "react-dom";
import { MdCardGiftcard, MdClose } from "react-icons/md";
import { BrandButton } from "../ui/BrandButton";

export function LoginGiftModal({
  open,
  onClose,
  onRetry,
  canClose = true,
  isSaving = false,
  errorMessage = "",
}) {
  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-primary/55 px-4 py-4"
      role="presentation"
      onMouseDown={(event) => {
        if (canClose && !isSaving && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-[2.25rem] border border-brand-secondary/15 bg-brand-surface shadow-[0_30px_90px_rgba(20,50,135,0.22)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-gift-modal-title"
      >
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-brand-gold via-brand-accent-light to-brand-secondary" />
        <div className="absolute -left-16 -top-16 h-40 w-40 rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="absolute -right-16 -bottom-20 h-44 w-44 rounded-full bg-brand-accent-light/12 blur-3xl" />

        <div className="relative p-5 sm:p-8 lg:p-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-secondary/15 bg-white shadow-[0_14px_30px_rgba(20,50,135,0.08)]">
                <MdCardGiftcard aria-hidden="true" className="h-6 w-6 text-brand-primary" />
              </div>
            </div>

            <BrandButton
              type="button"
              variant="ghost"
              radius="full"
              size="icon"
              motion="subtle"
              className="h-10 w-10 shrink-0 text-brand-primary hover:bg-brand-primary/10"
              aria-label="Cerrar aviso"
              disabled={!canClose || isSaving}
              onClick={onClose}
            >
              <MdClose aria-hidden="true" className="h-5 w-5" />
            </BrandButton>
          </div>

          <div className="mt-6 grid gap-6">
            <div className="grid gap-4">
              <h2
                id="login-gift-modal-title"
                className="max-w-2xl font-display text-[2.2rem] leading-[0.92] text-brand-primary sm:text-[3.25rem]"
              >
                ¡FELICITACIONES!
              </h2>

              <p className="max-w-2xl text-[1.02rem] leading-7 text-brand-primary sm:text-[1.08rem]">
                Por haberte registrado, recibirás un <strong>sobre de figuritas</strong> de
                regalo!
              </p>
              <p className="max-w-2xl text-[1.02rem] leading-7 text-brand-primary sm:text-[1.08rem]">
                Podés retirarlo en Hummus Time (Luis de la Torre 964) a partir del lunes 1 de
                junio, de 11:00 a 16:00 hs.
              </p>

              {errorMessage ? (
                <p className="max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                  {errorMessage}
                </p>
              ) : null}
            </div>

            <div className="flex justify-end">
              <BrandButton
                type="button"
                radius="full"
                size="lg"
                disabled={isSaving}
                onClick={errorMessage ? onRetry : onClose}
              >
                {isSaving ? "Registrando..." : errorMessage ? "Reintentar" : "¡Entendido!"}
              </BrandButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}
